import {
    useQuery,
    useMutation,
    useQueryClient
} from "@tanstack/react-query";
import axios from "@/axios.config";
import { ApiResponse } from "@/types/fetch.type";
import { 
    FineRule, 
    CreateFineRuleRequest, 
    UpdateFineRuleRequest,
    ApplicationFineCalculation,
    ApplicationFineDetail
} from "@/types";
import { AxiosError } from "axios";
import { notifications } from "@mantine/notifications";

// Fine Management APIs

export const useGetAllFineRules = () => {
    return useQuery<ApiResponse<FineRule[]>, AxiosError<{ message: string }>>({
        queryKey: ["fine-rules"],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<FineRule[]>>("/fines");
            return response.data;
        },
    });
};

export const useGetFineRulesByCouncil = (councilId: string) => {
    return useQuery<ApiResponse<FineRule[]>, AxiosError<{ message: string }>>({
        queryKey: ["fine-rules", councilId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<FineRule[]>>(`/fines/by-council/${councilId}`);
            return response.data;
        },
        enabled: !!councilId,
    });
};

export const useGetFineRuleById = (id: string) => {
    return useQuery<ApiResponse<FineRule>, AxiosError<{ message: string }>>({
        queryKey: ["fine-rules", id],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<FineRule>>(`/fines/${id}`);
            return response.data;
        },
        enabled: !!id,
    });
};

export const useCreateFineRule = () => {
    const queryClient = useQueryClient();
    
    return useMutation<ApiResponse<FineRule>, AxiosError<{ message: string }>, CreateFineRuleRequest>({
        mutationFn: async (params) => {
            const response = await axios.post<ApiResponse<FineRule>>("/fines", params);
            return response.data;
        },
        onSuccess: () => {
            notifications.show({
                title: 'Success',
                message: 'Fine rule created successfully',
                color: 'green'
            });
            queryClient.invalidateQueries({ queryKey: ["fine-rules"] });
        },
        onError: (error) => {
            notifications.show({
                title: 'Error',
                message: error.response?.data?.message || 'Failed to create fine rule',
                color: 'red'
            });
        }
    });
};

export const useUpdateFineRule = () => {
    const queryClient = useQueryClient();
    
    return useMutation<ApiResponse<FineRule>, AxiosError<{ message: string }>, UpdateFineRuleRequest>({
        mutationFn: async (params) => {
            const response = await axios.put<ApiResponse<FineRule>>(`/fines/${params.id}`, params);
            return response.data;
        },
        onSuccess: () => {
            notifications.show({
                title: 'Success',
                message: 'Fine rule updated successfully',
                color: 'green'
            });
            queryClient.invalidateQueries({ queryKey: ["fine-rules"] });
        },
        onError: (error) => {
            notifications.show({
                title: 'Error',
                message: error.response?.data?.message || 'Failed to update fine rule',
                color: 'red'
            });
        }
    });
};

export const useDeleteFineRule = () => {
    const queryClient = useQueryClient();
    
    return useMutation<ApiResponse<void>, AxiosError<{ message: string }>, string>({
        mutationFn: async (id) => {
            const response = await axios.delete<ApiResponse<void>>(`/fines/${id}`);
            return response.data;
        },
        onSuccess: () => {
            notifications.show({
                title: 'Success',
                message: 'Fine rule deleted successfully',
                color: 'green'
            });
            queryClient.invalidateQueries({ queryKey: ["fine-rules"] });
        },
        onError: (error) => {
            notifications.show({
                title: 'Error',
                message: error.response?.data?.message || 'Failed to delete fine rule',
                color: 'red'
            });
        }
    });
};

// Application Fine Calculation APIs

export const useCalculateApplicationFines = (applicationId: string) => {
    return useQuery<ApiResponse<ApplicationFineCalculation>, AxiosError<{ message: string }>>({
        queryKey: ["application-fines", applicationId, "calculate"],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<ApplicationFineCalculation>>(`/application-fines/${applicationId}/calculate`);
            return response.data;
        },
        enabled: !!applicationId,
    });
};

export const useGetStoredApplicationFines = (applicationId: string) => {
    return useQuery<ApiResponse<ApplicationFineDetail[]>, AxiosError<{ message: string }>>({
        queryKey: ["application-fines", applicationId, "stored"],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<ApplicationFineDetail[]>>(`/application-fines/${applicationId}/stored`);
            return response.data;
        },
        enabled: !!applicationId,
    });
};

export const useGetRealtimeApplicationFines = (applicationId: string) => {
    return useQuery<ApiResponse<ApplicationFineDetail[]>, AxiosError<{ message: string }>>({
        queryKey: ["application-fines", applicationId, "realtime"],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<ApplicationFineDetail[]>>(`/application-fines/${applicationId}/realtime`);
            return response.data;
        },
        enabled: !!applicationId,
    });
};

export interface AddManualFineRequest {
    applicationId: string;
    amount: number;
    dueDate: string;
    productCode: string;
    reasons: string[];
    description: string;
}

export const useAddManualFine = () => {
    const queryClient = useQueryClient();
    
    return useMutation<ApiResponse<string>, AxiosError<{ message: string }>, AddManualFineRequest>({
        mutationFn: async ({ applicationId, ...params }) => {
            const response = await axios.post<ApiResponse<string>>(`/application-fines/${applicationId}/manual`, params);
            return response.data;
        },
        onSuccess: () => {
            notifications.show({
                title: 'Success',
                message: 'Manual fine added successfully',
                color: 'green'
            });
            queryClient.invalidateQueries({ queryKey: ["application-fines"] });
        },
        onError: (error) => {
            notifications.show({
                title: 'Error',
                message: error.response?.data?.message || 'Failed to add manual fine',
                color: 'red'
            });
        }
    });
};

export const useUpdateStoredFineDetails = () => {
    const queryClient = useQueryClient();
    
    return useMutation<ApiResponse<string>, AxiosError<{ message: string }>, { applicationId: string; fines: ApplicationFineDetail[] }>({
        mutationFn: async ({ applicationId, fines }) => {
            const response = await axios.put<ApiResponse<string>>(`/application-fines/${applicationId}/update`, fines);
            return response.data;
        },
        onSuccess: () => {
            notifications.show({
                title: 'Success',
                message: 'Fine details updated successfully',
                color: 'green'
            });
            queryClient.invalidateQueries({ queryKey: ["application-fines"] });
        },
        onError: (error) => {
            notifications.show({
                title: 'Error',
                message: error.response?.data?.message || 'Failed to update fine details',
                color: 'red'
            });
        }
    });
};

export const useCheckPendingFinesStatus = (applicationId: string) => {
    return useQuery<ApiResponse<boolean>, AxiosError<{ message: string }>>({
        queryKey: ["application-fines", applicationId, "pending-status"],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<boolean>>(`/application-fines/${applicationId}/pending-status`);
            return response.data;
        },
        enabled: !!applicationId,
    });
};

export const useGetTotalPendingFineAmount = (applicationId: string) => {
    return useQuery<ApiResponse<number>, AxiosError<{ message: string }>>({
        queryKey: ["application-fines", applicationId, "pending-amount"],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<number>>(`/application-fines/${applicationId}/pending-amount`);
            return response.data;
        },
        enabled: !!applicationId,
    });
};

// Fine Payment APIs

export interface InitiateFinePaymentRequest {
    applicationId: string;
    paymentMethod: 'IREMBO_PAY' | 'MANUAL_UPLOAD';
    manualTransactionNumber?: string;
    receiptDocument?: File;
    bankId?: string;
}

export const useInitiateFinePayment = () => {
    const queryClient = useQueryClient();
    
    return useMutation<ApiResponse<any>, AxiosError<{ message: string }>, InitiateFinePaymentRequest>({
        mutationFn: async ({ applicationId, ...params }) => {
            const formData = new FormData();
            formData.append('certificateApplicationId', applicationId);
            formData.append('paymentMethod', params.paymentMethod);
            
            if (params.paymentMethod === 'MANUAL_UPLOAD') {
                if (params.manualTransactionNumber) {
                    formData.append('manualTransactionNumber', params.manualTransactionNumber);
                }
                if (params.receiptDocument) {
                    formData.append('receiptDocument', params.receiptDocument);
                }
                if (params.bankId) {
                    formData.append('bankId', params.bankId);
                }
            }
            
            const response = await axios.post(`/payments/initiate-fine-payment/${applicationId}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        },
        onSuccess: () => {
            notifications.show({
                title: 'Success',
                message: 'Fine payment initiated successfully',
                color: 'green'
            });
            queryClient.invalidateQueries({ queryKey: ["application-fines"] });
        },
        onError: (error) => {
            notifications.show({
                title: 'Error',
                message: error.response?.data?.message || 'Failed to initiate fine payment',
                color: 'red'
            });
        }
    });
};
